import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import useDownload from "../../../hooks/useDownload";
import {useSelector} from "react-redux";
import usePusher from "../../../hooks/usePusher";
import {programsService} from "../../../services/programsService/programsService";
import {PageErrorMessage} from "../../../components/PageErrorMessage";
import Loader from "../../../components/common/Loader";
import TextEditorContent from "../../../components/TextEditorContent";
import ProgressBar from "@ramonak/react-progress-bar";
import {IoCheckmark} from "react-icons/io5";
import ProgramMasterclassList from "./ProgramMasterclassList";
import ProgramCompleteInfoModal from "./modals/ProgramCompleteInfoModal";

const InstitutionUserProgramDetails = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [programDetails, setProgramDetails] = useState(null);
    const { id } = useParams();
    const downloadFile = useDownload();
    const navigate = useNavigate();
    const [showInfoModal, setShowInfoModal] = useState(false);
    const authUser = useSelector((state) => state?.auth?.auth);
    const pusher = usePusher();
    const [error, setError] = useState(null);
    const [isCertificateLoading, setIsCertificateLoading] = useState(false);

    const programDetailsData = async () => {
        setIsLoading(true);
        try {
            await programsService.getSingleProgramData(id).then((response) => {
                setProgramDetails(response?.data);
            });
            setIsLoading(false);
        } catch (error) {
            setError({
                statusCode: error.response.status,
                message: error.response.data.message,
            });
            setIsLoading(false);
        }
    };

    // useEffect of call all api function
    useEffect(() => {
        programDetailsData();
    }, [id]);

    const firstUncompletedMasterClass = programDetails?.master_classes.find(
        masterClass => masterClass.is_completed === false && masterClass.first_lesson !== null
    ) || null;

    const handleBeginProgram = () => {
        if (firstUncompletedMasterClass !== null) {
            navigate('/lessons/' + firstUncompletedMasterClass.first_lesson);
        }
    };

    const handleCloseInfoModal = () => {
        setShowInfoModal(false);
    };

    const handleViewCertificate = async () => {
        navigate('/programs/certificates/' + programDetails?.certificate?.id);
    };

    useEffect(() => {
        if (pusher) {
            const channel = pusher.subscribe('survey-completed.' + authUser.id);
            channel.bind('survey-completed', function(data) {
                setProgramDetails((prevState) => ({
                    ...prevState,
                    is_completed: true,
                }));
            });
        }
    }, [pusher]);

    if (error) {
        return <PageErrorMessage
            error={error}
            entityTitle="Program"
        />;
    }

    return (
        <>
            {isLoading ? (
                <>
                    <Loader />
                </>
            ) : (
                <>
                    <div className="container-fluid">
                        <div className="row mb-5 py-5 px-4 program__content">
                            <div className="col-md-6 text-center">
                                <div className="program__image">
                                    <img
                                        src={programDetails?.cover_image}
                                        alt={programDetails?.title}
                                        className="img-fluid"
                                    />
                                </div>
                            </div>

                            <div className="col-md-6 mb-4 mb-md-0">
                                <h1>{programDetails?.name}</h1>
                                <TextEditorContent
                                    text={programDetails?.description}
                                />
                                <div className="d-flex mt-3">
                                    <button
                                        className="btn btn-primary-info me-2"
                                        disabled={firstUncompletedMasterClass === null}
                                        onClick={handleBeginProgram}
                                    >
                                        Begin
                                    </button>
                                    {(!programDetails?.is_completed && programDetails?.progress === 100) &&
                                        <a target="_blank" href={programDetails?.survey_url}>
                                            <button
                                                className="btn btn-primary"
                                            >
                                                Complete Survey to View Your Certificate
                                            </button>
                                        </a>
                                    }
                                    {(!programDetails?.is_completed && programDetails?.progress !== 100) &&
                                        <button
                                            className="btn btn-primary"
                                            disabled={true}
                                            onClick={() => setShowInfoModal(true)}
                                        >
                                            Complete Survey to View Your Certificate
                                        </button>
                                    }
                                    {programDetails?.is_completed &&
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleViewCertificate}
                                        >
                                            View Your Certificate
                                        </button>
                                    }
                                </div>
                                <div className="mt-3">
                                    <div className="fs-6 lh-base fw-bolder">
                                        Program Progress
                                    </div>
                                    <ProgressBar completed={programDetails?.progress} bgColor="#1c64f2"
                                                 baseBgColor="#374151"
                                                 className={`w-100 progressbar-count ${programDetails?.progress > 5 ? '' : 'progressbar-count__hide-value'}`}/>
                                </div>
                                <div className="d-flex align-center mt-3">
                                    <div className="fs-6 lh-base fw-bolder">
                                        Credit Hours: {programDetails?.credit_hours}
                                    </div>
                                </div>
                                <div className="d-flex mt-3 gap-2">
                                    {!programDetails?.is_completed &&
                                        <button className="btn btn-success me-2"
                                                disabled={programDetails?.progress !== 100}
                                                onClick={() => setShowInfoModal(true)}>Complete</button>
                                    }
                                    {programDetails?.is_completed &&
                                        <button className="btn btn-secondary d-flex align-center" disabled={true}>
                                            Completed <IoCheckmark/>
                                        </button>
                                    }
                                </div>
                            </div>
                        </div>
                        <ProgramMasterclassList
                            masterClasses={programDetails?.master_classes}
                        />
                    </div>
                </>
            )}
            {showInfoModal &&
                <ProgramCompleteInfoModal
                    handleClose={handleCloseInfoModal}
                    program={programDetails}
                    onUpdateProgram={(updatedProgram) => setProgramDetails(updatedProgram)}
                />
            }
        </>
    );
};

export default InstitutionUserProgramDetails;